import { useGetInvitationApiV1Suspense } from "@/api/__generated__/api.schema";
import type { WorkspaceInvitation } from "@/api/__generated__/models";
import { EmailSignUpForm } from "@/components/email-sign-up-form";
import { WorkspaceIcon } from "@/components/workspace-icon";
import { routes } from "@/constants/url";
import { supabase } from "@/lib/supabase";
import { trackPixelReservedEvent } from "@/lib/track/pixel";
import { TrackingEvent } from "@/lib/track/track-event";
import { trackEvent } from "@/lib/track/track-event";
import { Alert, Loading, LogoDesktopBlack, Separator, ShadowBox, Typography } from "@backpac/monica";
import { useQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { z } from "zod";
import { AuthDisclaimer } from "../auth/components/auth-disclaimer";
import { SeparatorWithText } from "../auth/components/separator-with-text";
import { SignInWithGoogleButton } from "../auth/components/sign-in-with-google-button";
const WorkspaceInivitationPolicy = {
  BAD_ACCESS: "Bad access",
  DIFFERENT_EMAIL: (targetEmail: string) =>
    `It looks like you're signed in with a different account. To accept the invitation, please sign in with ${targetEmail}.`,
  EXPIRED: "Invitation expired",
};

// NOTE: http://localhost:5173/join-workspace?token=c86828a9-2f13-42f9-9823-06c60b69d90f
export function JoinWorkspacePage() {
  const navigate = useNavigate();

  return (
    <SearchParamsBoundary>
      {({ token, joinWorkspaceError }) => (
        <Suspense fallback={<Loading.FullHeight />}>
          <CheckPolicyBoundary token={token} joinWorkspaceError={joinWorkspaceError}>
            {({ invitation }) => (
              <JoinWorkspacePage.Layout>
                <div className="flex flex-col gap-6 w-full">
                  <LogoDesktopBlack />
                  <WorkspaceInfo invitation={invitation} />
                  <Separator className="mt-3 mx-[-24px] w-[calc(100%+48px)] desktop:w-full desktop:mx-0" />
                  <Typography asChild size="xs" weight={400} className="text-center w-full">
                    <span>
                      You're accepting an invitation sent to <span className="font-semibold">{invitation.email}</span>
                    </span>
                  </Typography>
                  <SignInWithGoogleButton
                    text="Sign up with Google"
                    redirectTo={`${window.location.origin}${routes.JOIN_WORKSPACE.callback}?token=${token}&email=${invitation.email}`}
                  />
                  {joinWorkspaceError != null ? <Alert theme="red">{joinWorkspaceError}</Alert> : null}
                  <SeparatorWithText>OR</SeparatorWithText>
                  <EmailSignUpForm
                    ctaText="Continue"
                    disabledEmail={true}
                    defaultValues={{ email: invitation.email }}
                    onSignupSuccess={(user) => {
                      trackEvent(TrackingEvent.SignUp, {
                        name: user.user_metadata.name,
                        email: user.email,
                        method: "email",
                      });
                      trackPixelReservedEvent("Lead");
                      navigate(`${routes.AUTH.signupEmailVerify}?email=${user.email}`, { replace: true });
                    }}
                    onSignInSuccess={(user) => {
                      trackEvent(TrackingEvent.SignIn, {
                        name: user.user_metadata.name,
                        email: user.email,
                        method: "email",
                      });
                      navigate(routes.WORKSPACE_NAVIGATOR.root, { replace: true });
                    }}
                  />

                  <AuthDisclaimer />
                </div>
              </JoinWorkspacePage.Layout>
            )}
          </CheckPolicyBoundary>
        </Suspense>
      )}
    </SearchParamsBoundary>
  );
}

function CheckPolicyBoundary({
  children,
  token,
  joinWorkspaceError,
}: {
  children: (data: { invitation: WorkspaceInvitation }) => React.ReactNode;
  token: string;
  joinWorkspaceError?: string;
}) {
  const {
    data: {
      data: { item: invitation },
    },
  } = useGetInvitationApiV1Suspense(token);

  const { data } = useQuery({
    queryKey: ["user"],
    queryFn: () => supabase.auth.getUser(),
  });
  const targetEmail = invitation.email;
  const signInEmail = data?.data.user?.email;

  // NOTE: if there is an error, do not check the policy
  if (joinWorkspaceError != null) {
    return children({ invitation });
  }

  if (signInEmail != null && signInEmail !== targetEmail) {
    return (
      <Navigate
        to={{
          pathname: routes.AUTH.error,
          search: `message=${encodeURIComponent(WorkspaceInivitationPolicy.DIFFERENT_EMAIL(targetEmail))}`,
        }}
        replace
      />
    );
  }

  if (signInEmail != null && signInEmail === targetEmail) {
    return (
      <Navigate
        to={{
          pathname: routes.WORKSPACE_NAVIGATOR.root,
        }}
        replace
      />
    );
  }

  return children({ invitation });
}

const JoinWorkspaceSearchParamsSchema = z.object({
  token: z.string(),
  joinWorkspaceError: z.string().optional(),
});

function SearchParamsBoundary({
  children,
}: { children: (params: z.infer<typeof JoinWorkspaceSearchParamsSchema>) => React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const { success, data } = JoinWorkspaceSearchParamsSchema.safeParse(Object.fromEntries(searchParams));

  if (success === false) {
    return (
      <Navigate
        to={{
          pathname: routes.AUTH.error,
          search: `message=${encodeURIComponent(WorkspaceInivitationPolicy.BAD_ACCESS)}`,
        }}
        replace
      />
    );
  }

  return children(data);
}

JoinWorkspacePage.Layout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-backgroundLight min-h-screen flex flex-col desktop:items-center desktop:justify-center">
      <div className="flex items-center justify-center px-4 py-5 desktop:px-0 w-full">
        <ShadowBox className="desktop:w-[480px] py-8 px-6 w-full">{children}</ShadowBox>
      </div>
    </div>
  );
};

function WorkspaceInfo({ invitation }: { invitation: WorkspaceInvitation }) {
  return (
    <>
      <Typography asChild size="lg" font="unbounded" className="text-center">
        <h1>Join the {invitation.workspace.name} workspace</h1>
      </Typography>

      <div className="flex items-center justify-between px-6 py-4 border border-borderLight rounded-[12px]">
        <div className="flex items-center gap-3">
          <WorkspaceIcon
            thumbnailUrl={invitation.workspace.thumbnailUrl}
            workspaceName={invitation.workspace.name}
            className="size-8"
          />
          <Typography size="xs" weight={600}>
            {invitation.workspace.name}
          </Typography>
        </div>
        <Typography size="xs" weight={400} color="textSubtle">
          {invitation.workspaceMemberCount ?? 1} members
        </Typography>
      </div>
    </>
  );
}
